import { fetchWithAuth } from "@/lib/api-helpers";

export interface RecentVisitor {
  userId: string;
  userName: string;
  userEmail: string;
  image: string | null;
  lastVisitedAt: string;
}

export type VisitRange = "7d" | "28d" | "90d" | "all";

export interface DailyVisits {
  /** UTC day, `YYYY-MM-DD`. */
  date: string;
  totalVisits: number;
  uniqueVisitors: number;
}

export interface PagePerson {
  userId: string;
  userName: string;
  userEmail: string;
  image: string | null;
  /** Last visit for a viewer, last edit for an editor, creation for the author. */
  at: string | null;
}

export interface PageVisitAnalytics {
  range: VisitRange;
  series: DailyVisits[];
  totalVisits: number;
  averageDurationMs: number | null;
}

// Deliberately not postWithAuth: that redirects to login on 401 and toasts on
// error. Visit tracking must never move the user or show UI.

export async function startPageVisit(
  blockId: string,
  workspaceId: string,
): Promise<string | null> {
  try {
    const res = await fetchWithAuth("/api/page-visits/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ blockId, workspaceId }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.visitId === "string" ? json.visitId : null;
  } catch {
    return null;
  }
}

/**
 * Fire-and-forget. `keepalive` lets the request outlive the page — sendBeacon
 * can't be used here because our cookie auth is cross-origin and its preflight
 * gets dropped during unload.
 */
export async function fetchRecentVisitors(
  blockId: string,
): Promise<RecentVisitor[]> {
  try {
    const res = await fetchWithAuth(`/api/page-visits/${blockId}/recent`);
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.visitors) ? json.visitors : [];
  } catch {
    return [];
  }
}

/** Range-independent, so it has its own route and is fetched once per page. */
export async function fetchPageViewers(blockId: string): Promise<PagePerson[]> {
  try {
    const res = await fetchWithAuth(`/api/page-visits/${blockId}/viewers`);
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.viewers) ? json.viewers : [];
  } catch {
    return [];
  }
}

/**
 * `"forbidden"` is distinguished from a plain failure because the endpoint is
 * editor-only: showing a viewer "no visits yet" would be a lie.
 */
export async function fetchPageVisitAnalytics(
  blockId: string,
  range: VisitRange,
): Promise<PageVisitAnalytics | "forbidden" | null> {
  try {
    const res = await fetchWithAuth(
      `/api/page-visits/${blockId}/analytics?range=${range}`,
    );
    if (res.status === 403) return "forbidden";
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json?.series) ? (json as PageVisitAnalytics) : null;
  } catch {
    return null;
  }
}

export function endPageVisit(visitId: string): void {
  try {
    void fetchWithAuth("/api/page-visits/end", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitId }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never surface a tracking failure
  }
}
